import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { APP_CONFIG, EnvConfig, EnvKey, KeycloakConfig, Region } from '../config/app-config';
import { readStoredRegion, REGION_STORAGE_KEY, resolveEnvKey } from '../config/resolve-env';

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  /** Build flavor — derived from compile-time environment.ts. */
  readonly buildEnv: EnvKey = resolveEnvKey();
  readonly isProd: boolean = this.buildEnv === 'prod';
  readonly isStaging: boolean = this.buildEnv === 'staging';
  readonly isDev: boolean = this.buildEnv === 'dev';

  private readonly region$ = new BehaviorSubject<Region>(readStoredRegion());

  /** Observable stream of the currently selected region. */
  readonly region: Observable<Region> = this.region$.asObservable();

  /** Current region (defaults to 'EU' on first load). */
  getRegion(): Region {
    return this.region$.value;
  }

  /**
   * Persist the chosen region and reload the page so Keycloak (initialized
   * at bootstrap from {@link resolveBootstrapConfig}) re-inits against the
   * new region's URLs. No-op if the region didn't change.
   */
  setRegion(region: Region): void {
    if (region === this.region$.value) {
      return;
    }
    localStorage.setItem(REGION_STORAGE_KEY, region);
    this.region$.next(region);
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  /** Resolved URL/keycloak config for the active env + region. */
  getConfig(): EnvConfig {
    return APP_CONFIG[this.buildEnv][this.region$.value];
  }

  /** Keycloak config for the current env + region. */
  get keycloak(): KeycloakConfig {
    return this.getConfig().keycloak;
  }

  /** Supergod backend base URL for the current env + region. */
  get supergodUrl(): string {
    return this.getConfig().supergodUrl;
  }

  /** Supergod cross-region (other server) base URL for the current env + region. */
  get supergodOtherServerUrl(): string {
    return this.getConfig().supergodOtherServerUrl;
  }

  /** Bhive (legacy backend) base URL for the current env + region. */
  get bhiveUrl(): string {
    return this.getConfig().bhiveUrl;
  }

  /** CubeJS REST API base URL for the current env + region. */
  get cubeUrl(): string {
    return this.getConfig().cubeUrl;
  }
  /** BOR service base URL (market-research matching/search) for the current env + region. */
  get borServiceUrl(): string {
    return this.getConfig().borServiceUrl;
  }
}
